/**
 * Central schema and contract for the Custom Report Builder.
 * Shared between backend (Hono worker) and frontend (React).
 */

export type CustomReportType =
  | 'labour_requirement'
  | 'actual_labour'
  | 'comparison'
  | 'project_summary'
  | 'approvals'
  | 'category_cost';

export type ColumnDataType = 'string' | 'number' | 'currency' | 'date' | 'status' | 'badge';

export interface CustomReportColumn {
  key: string;
  label: string;
  type: ColumnDataType;
  defaultSelected: boolean;
  totalable?: boolean;
}

export interface CustomReportTypeDef {
  type: CustomReportType;
  label: string;
  description: string;
  columns: CustomReportColumn[];
  groupByFields: { key: string; label: string }[];
  orderByFields: { key: string; label: string }[];
}

export type ConditionConnector = 'AND' | 'OR';

export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'greater_than'
  | 'less_than'
  | 'between';

export interface CustomCondition {
  id: string;
  connector: ConditionConnector;
  field: string;
  operator: ConditionOperator;
  value: string;
  value2?: string;
}

export const CONDITION_OPERATORS: { value: ConditionOperator; label: string }[] = [
  { value: 'equals', label: 'Equals (=)' },
  { value: 'not_equals', label: 'Does Not Equal (≠)' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater_than', label: 'Greater Than (>)' },
  { value: 'less_than', label: 'Less Than (<)' },
  { value: 'between', label: 'Between' },
];

export const CUSTOM_REPORT_DEFINITIONS: Record<CustomReportType, CustomReportTypeDef> = {
  labour_requirement: {
    type: 'labour_requirement',
    label: 'Labour Requirement Report',
    description: 'Detailed breakdown of submitted, approved, and rejected labour requirements by project, activity, and trade.',
    columns: [
      { key: 'requirement_number', label: 'Requirement Ref #', type: 'string', defaultSelected: true },
      { key: 'project_name', label: 'Project', type: 'string', defaultSelected: true },
      { key: 'work_date', label: 'Work Date', type: 'date', defaultSelected: true },
      { key: 'block_name', label: 'Block', type: 'string', defaultSelected: true },
      { key: 'activity_name', label: 'Activity', type: 'string', defaultSelected: true },
      { key: 'unit_name', label: 'Unit', type: 'string', defaultSelected: true },
      { key: 'quantity', label: 'Quantity', type: 'number', defaultSelected: true },
      { key: 'work_location', label: 'Work Location', type: 'string', defaultSelected: true },
      { key: 'worker_category_name', label: 'Worker Category', type: 'string', defaultSelected: true },
      { key: 'skill_level', label: 'Skill Level', type: 'string', defaultSelected: true },
      { key: 'required_headcount', label: 'Headcount', type: 'number', defaultSelected: true, totalable: true },
      { key: 'planned_hours', label: 'Hours', type: 'number', defaultSelected: true, totalable: true },
      { key: 'planned_rate', label: 'Daily Rate (₹/day)', type: 'currency', defaultSelected: true },
      { key: 'planned_amount', label: 'Total Amount (₹)', type: 'currency', defaultSelected: true, totalable: true },
      { key: 'shift_name', label: 'Shift', type: 'string', defaultSelected: true },
      { key: 'priority', label: 'Priority', type: 'badge', defaultSelected: true },
      { key: 'assigned_pm_name', label: 'Assigned PM', type: 'string', defaultSelected: true },
      { key: 'status', label: 'Requirement Status', type: 'status', defaultSelected: true },
      { key: 'manager_decision', label: 'PM Approval', type: 'status', defaultSelected: true },
      { key: 'manager_remarks', label: 'PM Remarks', type: 'string', defaultSelected: true },
      { key: 'cluster_decision', label: 'CH Approval', type: 'status', defaultSelected: true },
      { key: 'cluster_remarks', label: 'CH Remarks', type: 'string', defaultSelected: true },
    ],
    groupByFields: [
      { key: '', label: 'None' },
      { key: 'project_name', label: 'Project' },
      { key: 'work_date', label: 'Work Date' },
      { key: 'block_name', label: 'Block' },
      { key: 'activity_name', label: 'Activity' },
      { key: 'worker_category_name', label: 'Worker Category' },
      { key: 'status', label: 'Status' },
    ],
    orderByFields: [
      { key: 'work_date', label: 'Work Date' },
      { key: 'requirement_number', label: 'Requirement Ref #' },
      { key: 'project_name', label: 'Project' },
      { key: 'block_name', label: 'Block' },
      { key: 'quantity', label: 'Quantity' },
      { key: 'required_headcount', label: 'Headcount' },
      { key: 'planned_amount', label: 'Total Amount' },
      { key: 'status', label: 'Status' },
    ],
  },

  actual_labour: {
    type: 'actual_labour',
    label: 'Actual Labour Report',
    description: 'Actual labour deployed on site with hours worked, rate, cost, and approval statuses.',
    columns: [
      { key: 'actual_number', label: 'Actual Ref #', type: 'string', defaultSelected: true },
      { key: 'requirement_number', label: 'Requirement Ref #', type: 'string', defaultSelected: true },
      { key: 'project_name', label: 'Project', type: 'string', defaultSelected: true },
      { key: 'work_date', label: 'Work Date', type: 'date', defaultSelected: true },
      { key: 'block_name', label: 'Block', type: 'string', defaultSelected: true },
      { key: 'activity_name', label: 'Activity', type: 'string', defaultSelected: true },
      { key: 'unit_name', label: 'Unit', type: 'string', defaultSelected: true },
      { key: 'completed_quantity', label: 'Completed Quantity', type: 'number', defaultSelected: true },
      { key: 'worker_category_name', label: 'Worker Category', type: 'string', defaultSelected: true },
      { key: 'skill_level', label: 'Skill Level', type: 'string', defaultSelected: true },
      { key: 'actual_count', label: 'Actual Headcount', type: 'number', defaultSelected: true, totalable: true },
      { key: 'time_in', label: 'Time In', type: 'string', defaultSelected: true },
      { key: 'time_out', label: 'Time Out', type: 'string', defaultSelected: true },
      { key: 'duration_hours', label: 'Hours', type: 'number', defaultSelected: true, totalable: true },
      { key: 'rate', label: 'Actual Daily Rate (₹/day)', type: 'currency', defaultSelected: true },
      { key: 'worker_total', label: 'Total Cost (₹)', type: 'currency', defaultSelected: true, totalable: true },
      { key: 'status', label: 'Approval Status', type: 'status', defaultSelected: true },
      { key: 'remarks', label: 'Remarks', type: 'string', defaultSelected: true },
    ],
    groupByFields: [
      { key: '', label: 'None' },
      { key: 'project_name', label: 'Project' },
      { key: 'work_date', label: 'Work Date' },
      { key: 'block_name', label: 'Block' },
      { key: 'activity_name', label: 'Activity' },
      { key: 'worker_category_name', label: 'Worker Category' },
      { key: 'status', label: 'Status' },
    ],
    orderByFields: [
      { key: 'work_date', label: 'Work Date' },
      { key: 'actual_number', label: 'Actual Ref #' },
      { key: 'project_name', label: 'Project' },
      { key: 'completed_quantity', label: 'Completed Quantity' },
      { key: 'actual_count', label: 'Actual Headcount' },
      { key: 'worker_total', label: 'Total Cost' },
      { key: 'status', label: 'Status' },
    ],
  },

  comparison: {
    type: 'comparison',
    label: 'Requirement vs Actual Comparison',
    description: 'Compare planned headcount and cost against actual site deployments with variance calculations.',
    columns: [
      { key: 'project_name', label: 'Project', type: 'string', defaultSelected: true },
      { key: 'work_date', label: 'Work Date', type: 'date', defaultSelected: true },
      { key: 'block_name', label: 'Block', type: 'string', defaultSelected: true },
      { key: 'activity_name', label: 'Activity', type: 'string', defaultSelected: true },
      { key: 'unit_name', label: 'Unit', type: 'string', defaultSelected: true },
      { key: 'worker_category_name', label: 'Worker Category', type: 'string', defaultSelected: true },
      { key: 'skill_level', label: 'Skill Level', type: 'string', defaultSelected: true },
      { key: 'planned_headcount', label: 'Planned Headcount', type: 'number', defaultSelected: true, totalable: true },
      { key: 'actual_headcount', label: 'Actual Headcount', type: 'number', defaultSelected: true, totalable: true },
      { key: 'headcount_variance', label: 'Headcount Variance', type: 'number', defaultSelected: true, totalable: true },
      { key: 'planned_cost', label: 'Planned Cost (₹)', type: 'currency', defaultSelected: true, totalable: true },
      { key: 'actual_cost', label: 'Actual Cost (₹)', type: 'currency', defaultSelected: true, totalable: true },
      { key: 'cost_variance', label: 'Cost Variance (₹)', type: 'currency', defaultSelected: true, totalable: true },
      { key: 'cost_variance_status', label: 'Budget Variance Status', type: 'badge', defaultSelected: true },
    ],
    groupByFields: [
      { key: '', label: 'None' },
      { key: 'project_name', label: 'Project' },
      { key: 'work_date', label: 'Work Date' },
      { key: 'block_name', label: 'Block' },
      { key: 'worker_category_name', label: 'Worker Category' },
      { key: 'cost_variance_status', label: 'Budget Status' },
    ],
    orderByFields: [
      { key: 'work_date', label: 'Work Date' },
      { key: 'project_name', label: 'Project' },
      { key: 'headcount_variance', label: 'Headcount Variance' },
      { key: 'cost_variance', label: 'Cost Variance' },
      { key: 'planned_cost', label: 'Planned Cost' },
      { key: 'actual_cost', label: 'Actual Cost' },
    ],
  },

  project_summary: {
    type: 'project_summary',
    label: 'Project Labour Summary',
    description: 'High-level project aggregates of requirements, actual deployments, and overall cost variances.',
    columns: [
      { key: 'project_name', label: 'Project Name', type: 'string', defaultSelected: true },
      { key: 'client_name', label: 'Client Name', type: 'string', defaultSelected: true },
      { key: 'project_status', label: 'Project Status', type: 'status', defaultSelected: true },
      { key: 'plan_entries', label: 'Requirements Count', type: 'number', defaultSelected: true, totalable: true },
      { key: 'actual_entries', label: 'Actual Entries Count', type: 'number', defaultSelected: true, totalable: true },
      { key: 'planned_manpower', label: 'Planned Manpower', type: 'number', defaultSelected: true, totalable: true },
      { key: 'actual_regular_manpower', label: 'Actual Regular Manpower', type: 'number', defaultSelected: true, totalable: true },
      { key: 'actual_adhoc_manpower', label: 'Actual Adhoc Manpower', type: 'number', defaultSelected: true, totalable: true },
      { key: 'total_actual_manpower', label: 'Total Actual Manpower', type: 'number', defaultSelected: true, totalable: true },
      { key: 'manpower_variance', label: 'Manpower Variance', type: 'number', defaultSelected: true, totalable: true },
      { key: 'planned_amount', label: 'Planned Cost (₹)', type: 'currency', defaultSelected: true, totalable: true },
      { key: 'actual_amount', label: 'Actual Cost (₹)', type: 'currency', defaultSelected: true, totalable: true },
      { key: 'amount_variance', label: 'Cost Variance (₹)', type: 'currency', defaultSelected: true, totalable: true },
    ],
    groupByFields: [
      { key: '', label: 'None' },
      { key: 'project_status', label: 'Project Status' },
      { key: 'client_name', label: 'Client Name' },
    ],
    orderByFields: [
      { key: 'project_name', label: 'Project Name' },
      { key: 'planned_amount', label: 'Planned Cost' },
      { key: 'actual_amount', label: 'Actual Cost' },
      { key: 'amount_variance', label: 'Cost Variance' },
      { key: 'total_actual_manpower', label: 'Total Actual Manpower' },
    ],
  },

  approvals: {
    type: 'approvals',
    label: 'Approval Status Report',
    description: 'Audit log of PM approvals, Cluster Head approvals, timestamps, and remarks across records.',
    columns: [
      { key: 'record_type', label: 'Record Type', type: 'string', defaultSelected: true },
      { key: 'reference_number', label: 'Reference Number', type: 'string', defaultSelected: true },
      { key: 'project_name', label: 'Project', type: 'string', defaultSelected: true },
      { key: 'work_date', label: 'Work Date', type: 'date', defaultSelected: true },
      { key: 'overall_status', label: 'Overall Status', type: 'status', defaultSelected: true },
      { key: 'current_approval_level', label: 'Approval Level', type: 'number', defaultSelected: true },
      { key: 'manager_decision', label: 'PM Approval', type: 'status', defaultSelected: true },
      { key: 'manager_decided_at', label: 'PM Approval Date', type: 'date', defaultSelected: true },
      { key: 'manager_remarks', label: 'PM Remarks', type: 'string', defaultSelected: true },
      { key: 'cluster_decision', label: 'CH Approval', type: 'status', defaultSelected: true },
      { key: 'cluster_decided_at', label: 'CH Approval Date', type: 'date', defaultSelected: true },
      { key: 'cluster_remarks', label: 'CH Remarks', type: 'string', defaultSelected: true },
    ],
    groupByFields: [
      { key: '', label: 'None' },
      { key: 'record_type', label: 'Record Type' },
      { key: 'project_name', label: 'Project' },
      { key: 'overall_status', label: 'Overall Status' },
      { key: 'manager_decision', label: 'PM Approval' },
      { key: 'cluster_decision', label: 'CH Approval' },
    ],
    orderByFields: [
      { key: 'work_date', label: 'Work Date' },
      { key: 'reference_number', label: 'Reference Number' },
      { key: 'project_name', label: 'Project' },
      { key: 'overall_status', label: 'Overall Status' },
      { key: 'manager_decided_at', label: 'PM Approval Date' },
      { key: 'cluster_decided_at', label: 'CH Approval Date' },
    ],
  },

  category_cost: {
    type: 'category_cost',
    label: 'Worker Category and Cost Report',
    description: 'Breakdown of headcount, regular vs adhoc classification, and expenditures by worker trade.',
    columns: [
      { key: 'worker_category_name', label: 'Worker Category', type: 'string', defaultSelected: true },
      { key: 'skill_level', label: 'Skill Level', type: 'string', defaultSelected: true },
      { key: 'labour_type', label: 'Classification', type: 'badge', defaultSelected: true },
      { key: 'project_name', label: 'Project', type: 'string', defaultSelected: true },
      { key: 'planned_headcount', label: 'Planned Headcount', type: 'number', defaultSelected: true, totalable: true },
      { key: 'actual_headcount', label: 'Actual Headcount', type: 'number', defaultSelected: true, totalable: true },
      { key: 'headcount_variance', label: 'Headcount Variance', type: 'number', defaultSelected: true, totalable: true },
      { key: 'average_rate', label: 'Average Daily Rate (₹/day)', type: 'currency', defaultSelected: true },
      { key: 'planned_amount', label: 'Planned Cost (₹)', type: 'currency', defaultSelected: true, totalable: true },
      { key: 'actual_amount', label: 'Actual Cost (₹)', type: 'currency', defaultSelected: true, totalable: true },
      { key: 'cost_variance', label: 'Cost Variance (₹)', type: 'currency', defaultSelected: true, totalable: true },
    ],
    groupByFields: [
      { key: '', label: 'None' },
      { key: 'skill_level', label: 'Skill Level' },
      { key: 'labour_type', label: 'Classification' },
      { key: 'project_name', label: 'Project' },
    ],
    orderByFields: [
      { key: 'worker_category_name', label: 'Worker Category' },
      { key: 'project_name', label: 'Project' },
      { key: 'planned_amount', label: 'Planned Cost' },
      { key: 'actual_amount', label: 'Actual Cost' },
      { key: 'cost_variance', label: 'Cost Variance' },
      { key: 'actual_headcount', label: 'Actual Headcount' },
    ],
  },
};

export type UnifiedColumnGroup = 'common' | 'requirement' | 'actual';

export interface UnifiedReportColumn extends CustomReportColumn {
  group: UnifiedColumnGroup;
}

export const UNIFIED_COLUMN_GROUPS: { key: UnifiedColumnGroup; label: string }[] = [
  { key: 'common', label: 'Common' },
  { key: 'requirement', label: 'Requirement' },
  { key: 'actual', label: 'Actual Labour' },
];

export const UNIFIED_REPORT_COLUMNS: UnifiedReportColumn[] = [
  { key: 'record_type', label: 'Record Type', type: 'string', defaultSelected: true, group: 'common' },
  { key: 'reference_number', label: 'Reference Number', type: 'string', defaultSelected: true, group: 'common' },
  { key: 'project_name', label: 'Project', type: 'string', defaultSelected: true, group: 'common' },
  { key: 'work_date', label: 'Work Date', type: 'date', defaultSelected: true, group: 'common' },
  { key: 'block_name', label: 'Block', type: 'string', defaultSelected: true, group: 'common' },
  { key: 'activity_name', label: 'Activity', type: 'string', defaultSelected: true, group: 'common' },
  { key: 'unit_name', label: 'Unit', type: 'string', defaultSelected: false, group: 'common' },
  { key: 'work_location', label: 'Work Location', type: 'string', defaultSelected: false, group: 'common' },
  { key: 'worker_category_name', label: 'Worker Category', type: 'string', defaultSelected: true, group: 'common' },
  { key: 'trade_name', label: 'Trade', type: 'string', defaultSelected: false, group: 'common' },
  { key: 'skill_level', label: 'Skill Level', type: 'string', defaultSelected: true, group: 'common' },
  { key: 'headcount', label: 'Headcount', type: 'number', defaultSelected: true, totalable: true, group: 'common' },
  { key: 'quantity', label: 'Quantity', type: 'number', defaultSelected: false, totalable: true, group: 'common' },
  { key: 'hours', label: 'Hours', type: 'number', defaultSelected: true, totalable: true, group: 'common' },
  { key: 'rate', label: 'Daily Rate (₹/day)', type: 'currency', defaultSelected: false, group: 'common' },
  { key: 'total_amount', label: 'Total Amount', type: 'currency', defaultSelected: true, totalable: true, group: 'common' },
  { key: 'shift_name', label: 'Shift', type: 'string', defaultSelected: false, group: 'common' },
  { key: 'priority', label: 'Priority', type: 'badge', defaultSelected: false, group: 'common' },
  { key: 'requirement_status', label: 'Requirement Status', type: 'status', defaultSelected: true, group: 'requirement' },
  { key: 'assigned_pm_name', label: 'Assigned PM', type: 'string', defaultSelected: false, group: 'requirement' },
  { key: 'manager_decision', label: 'PM Approval', type: 'status', defaultSelected: true, group: 'requirement' },
  { key: 'manager_remarks', label: 'PM Remarks', type: 'string', defaultSelected: false, group: 'requirement' },
  { key: 'cluster_decision', label: 'CH Approval', type: 'status', defaultSelected: true, group: 'requirement' },
  { key: 'cluster_remarks', label: 'CH Remarks', type: 'string', defaultSelected: false, group: 'requirement' },
  { key: 'requirement_date', label: 'Requirement Date', type: 'date', defaultSelected: false, group: 'requirement' },
  { key: 'work_end_date', label: 'Work End Date', type: 'date', defaultSelected: false, group: 'requirement' },
  { key: 'contractor_name', label: 'Contractor', type: 'string', defaultSelected: false, group: 'requirement' },
  { key: 'current_approval_level', label: 'Current Approval Level', type: 'number', defaultSelected: false, group: 'requirement' },
  { key: 'actual_number', label: 'Actual Labour Reference', type: 'string', defaultSelected: true, group: 'actual' },
  { key: 'linked_requirement_number', label: 'Linked Requirement Reference', type: 'string', defaultSelected: false, group: 'actual' },
  { key: 'actual_headcount', label: 'Actual Headcount', type: 'number', defaultSelected: true, totalable: true, group: 'actual' },
  { key: 'actual_quantity', label: 'Actual Quantity', type: 'number', defaultSelected: false, totalable: true, group: 'actual' },
  { key: 'actual_hours', label: 'Actual Hours', type: 'number', defaultSelected: true, totalable: true, group: 'actual' },
  { key: 'actual_rate', label: 'Actual Daily Rate (₹/day)', type: 'currency', defaultSelected: false, group: 'actual' },
  { key: 'actual_amount', label: 'Actual Amount', type: 'currency', defaultSelected: true, totalable: true, group: 'actual' },
  { key: 'actual_time_in', label: 'Time In', type: 'string', defaultSelected: false, group: 'actual' },
  { key: 'actual_time_out', label: 'Time Out', type: 'string', defaultSelected: false, group: 'actual' },
  { key: 'actual_status', label: 'Actual Labour Status', type: 'status', defaultSelected: true, group: 'actual' },
  { key: 'actual_remarks', label: 'Actual Remarks', type: 'string', defaultSelected: false, group: 'actual' },
  { key: 'actual_manager_decision', label: 'Actual PM Approval', type: 'status', defaultSelected: true, group: 'actual' },
  { key: 'actual_manager_remarks', label: 'Actual PM Remarks', type: 'string', defaultSelected: false, group: 'actual' },
  { key: 'actual_cluster_decision', label: 'Actual CH Approval', type: 'status', defaultSelected: true, group: 'actual' },
  { key: 'actual_cluster_remarks', label: 'Actual CH Remarks', type: 'string', defaultSelected: false, group: 'actual' },
  { key: 'actual_current_approval_level', label: 'Actual Approval Level', type: 'number', defaultSelected: false, group: 'actual' },
];

export const UNIFIED_REPORT_COLUMN_KEYS = new Set(UNIFIED_REPORT_COLUMNS.map((column) => column.key));
